import Button from '@/components/Base/Button';
import { FormInput, FormLabel, FormTextarea } from '@/components/Base/Form';
import Lucide from '@/components/Base/Lucide';
import {
  PermissionDefinition,
  PermissionModule,
  Role,
  UiSurface,
} from '@/stores/rolesSlice';
import { getEncryptedClientId } from '@/utils/helper';
import { useMemo, useState } from 'react';

const stamp = (iso?: string): string =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const who = (actor?: { name?: string; email?: string } | null): string =>
  actor?.name || actor?.email || 'system';

/** Matches the @Length(0, 280) cap on UpdateRoleDTO/CreateRoleDTO. */
const DESCRIPTION_MAX = 280;

/** Advanced permission list, hidden for now. See the block near the bottom. */
const SHOW_ADVANCED = false;

type Level = 'none' | 'view' | 'edit';

const LEVEL_LABELS: Record<Level, string> = {
  none: 'No access',
  view: 'View',
  edit: 'View & edit',
};

interface RoleBuilderProps {
  /** The screens this admin can hand out. What the builder renders. */
  surfaces: UiSurface[];
  /** Permission model behind the surfaces — used for the dependency closure. */
  catalogue: PermissionModule[];
  /** Keepme staff get the raw permission list as well. */
  isPlatform?: boolean;
  /** Editing an existing role, or undefined when creating. */
  role?: Role | null;
  /** Read-only view — used for system roles, which can only be cloned. */
  readOnly?: boolean;
  saving?: boolean;
  onCancel: () => void;
  onSave: (payload: {
    name: string;
    description: string;
    permissions: string[];
    surfaces: string[];
  }) => void;
}

/** Permissions a surface hands out at a given level. */
const keysAt = (surface: UiSurface, level: Level): string[] => {
  if (level === 'none') return [];
  if (level === 'view') return surface.view;
  return [...surface.view, ...(surface.edit ?? [])];
};

/**
 * Mirrors the server's `expandForCustomRole`, so the builder can show what a
 * choice pulls in before saving rather than after.
 */
const buildClosure = (catalogue: PermissionModule[]) => {
  const definitions = new Map<string, PermissionDefinition>();
  for (const mod of catalogue) {
    for (const perm of mod.permissions) definitions.set(perm.key, perm);
  }

  const closure = (keys: string[]): Set<string> => {
    const out = new Set<string>();
    const queue = [...keys];
    while (queue.length) {
      const k = queue.pop() as string;
      if (out.has(k)) continue;
      out.add(k);
      const def = definitions.get(k);
      queue.push(...(def?.implies ?? []), ...(def?.requires ?? []));
    }
    return out;
  };

  return { closure };
};

/**
 * Recover the choices behind a saved role. A role stores its closed set, so
 * anything another chosen surface already pulls in is treated as derived —
 * one-way only, or a mutual pair would cancel out and both vanish.
 */
const recoverLevels = (
  surfaces: UiSurface[],
  stored: Set<string>,
  closure: (keys: string[]) => Set<string>
): { levels: Record<string, Level>; addOns: Set<string> } => {
  const held: Record<string, Level> = {};
  for (const surface of surfaces) {
    const hasView = surface.view.every((k) => stored.has(k));
    if (!hasView) {
      held[surface.id] = 'none';
      continue;
    }
    const hasEdit =
      !!surface.edit?.length && surface.edit.every((k) => stored.has(k));
    held[surface.id] = hasEdit ? 'edit' : 'view';
  }

  const levels: Record<string, Level> = { ...held };
  for (const surface of surfaces) {
    const level = held[surface.id];
    if (level === 'none') continue;
    const mine = keysAt(surface, level);

    for (const other of surfaces) {
      if (other.id === surface.id || held[other.id] === 'none') continue;
      const theirs = closure(keysAt(other, held[other.id]));
      const pulledIn = mine.every((k) => theirs.has(k));
      if (!pulledIn) continue;
      // One-way only: if each covers the other, both are real choices.
      const mutual = keysAt(other, held[other.id]).every((k) =>
        closure(mine).has(k)
      );
      if (!mutual) {
        levels[surface.id] = 'none';
        break;
      }
    }
  }

  const addOns = new Set<string>();
  for (const surface of surfaces) {
    for (const addOn of surface.addOns ?? []) {
      if (stored.has(addOn.key)) addOns.add(addOn.key);
    }
  }

  return { levels, addOns };
};

const RoleBuilder = ({
  surfaces,
  catalogue,
  isPlatform = false,
  role,
  readOnly = false,
  saving = false,
  onCancel,
  onSave,
}: RoleBuilderProps) => {
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [showAdvanced, setShowAdvanced] = useState(false);

  /** Null until a client is open, in which case no "Show me" links render. */
  const clientSegment = useMemo(() => getEncryptedClientId(), []);

  const { closure } = useMemo(
    () => buildClosure(catalogue),
    [catalogue]
  );

  const stored = useMemo(
    () => new Set(role?.permissions ?? []),
    [role?.permissions]
  );

  const recovered = useMemo(() => {
    // A role that states what it reaches needs no guessing; only older ones do.
    // An empty array is a statement — Beacon reaches nothing — so test for the
    // array, not its length.
    const ticked = role?.surfaces;
    if (!Array.isArray(ticked)) return recoverLevels(surfaces, stored, closure);
    const levels: Record<string, Level> = {};
    for (const surface of surfaces) {
      if (!ticked.includes(surface.id)) {
        levels[surface.id] = 'none';
        continue;
      }
      levels[surface.id] =
        !!surface.edit?.length && surface.edit.every((k) => stored.has(k))
          ? 'edit'
          : 'view';
    }
    const addOns = new Set(
      surfaces
        .flatMap((s) => s.addOns ?? [])
        .map((a) => a.key)
        .filter((k) => stored.has(k))
    );
    return { levels, addOns };
  }, [role?.surfaces, surfaces, stored, closure]);

  /** What the admin chose, per screen. */
  const [levels, setLevels] = useState<Record<string, Level>>(
    () => recovered.levels
  );
  const [addOns, setAddOns] = useState<Set<string>>(() => recovered.addOns);

  /** Held but claimed by no screen. Carried through so a save cannot strip it. */
  const carried = useMemo(() => {
    const claimed = new Set(
      surfaces.flatMap((s) => [
        ...s.view,
        ...(s.edit ?? []),
        ...(s.addOns ?? []).map((a) => a.key),
      ])
    );
    return [...stored].filter((k) => !claimed.has(k));
  }, [surfaces, stored]);

  /** Extra keys a platform actor picked from the raw catalogue. */
  const [advanced, setAdvanced] = useState<Set<string>>(() => new Set());

  /** Everything explicitly chosen, before the dependency closure. */
  const chosen = useMemo(() => {
    // Unconditional: a screen can be on via the closure, and its extras still
    // need to be tickable. setLevel('none') clears them.
    const keys: string[] = [...carried, ...advanced, ...addOns];
    for (const surface of surfaces) {
      keys.push(...keysAt(surface, levels[surface.id] ?? 'none'));
    }
    return keys;
  }, [surfaces, levels, addOns, carried, advanced]);

  /** Everything that will actually be saved. */
  const selected = useMemo(() => closure(chosen), [chosen, closure]);

  /**
   * The level each screen ends up at.
   *
   * Exactly what was ticked. Deriving it from the saved keys instead reads back
   * screens nobody asked for: Locations needs branches:read + regions:read,
   * both of which Overview already carries, so one tick lit up two rows.
   */
  const effective = useMemo(() => {
    const out: Record<string, Level> = {};
    for (const surface of surfaces) out[surface.id] = levels[surface.id] ?? 'none';
    return out;
  }, [surfaces, levels]);

  /** Which screen pulled this one in, for the row's "Comes with" line. */
  /** Nothing is inferred any more, so no screen arrives via another. */
  const reasonFor = (_surface: UiSurface): string | null => null;

  const setLevel = (surfaceId: string, level: Level) => {
    if (readOnly) return;
    setLevels((prev) => ({ ...prev, [surfaceId]: level }));
    if (level !== 'none') return;
    // Otherwise a hidden add-on would keep pulling the screen back on.
    const extras = surfaces
      .find((s) => s.id === surfaceId)
      ?.addOns?.map((a) => a.key);
    if (!extras?.length) return;
    setAddOns((prev) => {
      const next = new Set(prev);
      extras.forEach((k) => next.delete(k));
      return next;
    });
  };

  const toggleAddOn = (key: string) => {
    if (readOnly) return;
    setAddOns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  /** Sections in the order the server sent them. */
  const sections = useMemo(() => {
    const bySection = new Map<string, UiSurface[]>();
    for (const surface of surfaces) {
      if (!bySection.has(surface.section)) bySection.set(surface.section, []);
      bySection.get(surface.section)?.push(surface);
    }
    return [...bySection.entries()];
  }, [surfaces]);

  const grantedCount = surfaces.filter(
    (s) => (effective[s.id] ?? 'none') !== 'none'
  ).length;

  const canSave = name.trim().length >= 2 && selected.size > 0 && !saving;

  const renderLevelPicker = (surface: UiSurface) => {
    const chosenLevel = levels[surface.id] ?? 'none';
    const actual = effective[surface.id] ?? 'none';
    const options: Level[] = surface.edit?.length
      ? ['none', 'view', 'edit']
      : ['none', 'view'];

    return (
      // Fixed track, group left-aligned inside it: a two-option row would
      // otherwise end flush right and so begin further right than the
      // three-option rows, leaving the column ragged.
      <div className='flex w-[16.5rem] shrink-0 justify-start'>
        <div className='flex overflow-hidden rounded-md border border-slate-200 dark:border-darkmode-400'>
          {options.map((option) => {
            const isOn = actual === option;
            // The closure put this on; clicking a lower level would not stick.
            const forced = isOn && chosenLevel !== option;
            return (
              <button
                key={option}
                type='button'
                disabled={readOnly}
                onClick={() => setLevel(surface.id, option)}
                className={`min-w-[5.5rem] px-2.5 py-1 text-center text-xs transition-colors ${
                  isOn
                    ? 'bg-primary text-white'
                    : 'bg-white text-slate-500 hover:bg-slate-50 dark:bg-darkmode-600 dark:hover:bg-darkmode-500'
                } ${readOnly ? 'cursor-not-allowed opacity-70' : ''} ${
                  forced ? 'opacity-90' : ''
                }`}
              >
                {LEVEL_LABELS[option]}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderToggle = (surface: UiSurface) => {
    const actual = (effective[surface.id] ?? 'none') !== 'none';
    return (
      <input
        type='checkbox'
        className='mt-0.5 shrink-0'
        checked={actual}
        disabled={readOnly}
        onChange={() => setLevel(surface.id, actual ? 'none' : 'view')}
      />
    );
  };

  return (
    <div className='flex flex-col gap-5'>
      {readOnly && (
        <div className='flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-xs'>
          <Lucide icon='Lock' className='mt-0.5 h-4 w-4 shrink-0' />
          <span>
            This is a reserved system role and cannot be edited. Clone it to
            create an editable copy with the same starting permissions.
          </span>
        </div>
      )}

      <div className='grid gap-4'>
        <div className='sm:max-w-sm'>
          <FormLabel htmlFor='role-name'>Role name</FormLabel>
          <FormInput
            id='role-name'
            type='text'
            value={name}
            disabled={readOnly}
            placeholder='e.g. Regional Manager'
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        {/* Full width on its own row: a one-line textarea beside the name
            could not show even a short role description without scrolling. */}
        <div>
          <div className='mb-1.5 flex items-baseline justify-between gap-2'>
            <FormLabel htmlFor='role-desc' className='mb-0'>
              Description
            </FormLabel>
            {!readOnly && (
              <span
                className={`text-xs tabular-nums ${
                  description.length > DESCRIPTION_MAX - 30
                    ? 'text-warning'
                    : 'text-slate-400'
                }`}
              >
                {description.length}/{DESCRIPTION_MAX}
              </span>
            )}
          </div>
          <FormTextarea
            id='role-desc'
            rows={3}
            maxLength={DESCRIPTION_MAX}
            value={description}
            disabled={readOnly}
            placeholder='Who is this role for, and what should they be able to do?'
            className='min-h-[92px] resize-y leading-relaxed'
            onChange={(e) => setDescription(e.target.value)}
          />
          {!readOnly && (
            <p className='mt-1.5 text-xs text-slate-400'>
              Optional, but it appears in the roles list and helps whoever
              assigns this role later.
            </p>
          )}
        </div>
      </div>

      <div>
        <div className='mb-1 flex items-center justify-between'>
          <FormLabel className='mb-0'>What this role can reach</FormLabel>
          <span className='text-xs text-slate-500'>
            {grantedCount} of {surfaces.length} screens
          </span>
        </div>
        <p className='mb-2 text-xs text-slate-400'>
          Only the screens you can reach yourself are listed. Turning one on may
          switch on another it depends on — the row says which.
        </p>

        <div className='max-h-[45vh] overflow-y-auto rounded-md border border-slate-200/60 dark:border-darkmode-400'>
          {surfaces.length === 0 && (
            <p className='px-3 py-8 text-center text-sm text-slate-500'>
              You do not have access to any screens you could pass on.
            </p>
          )}

          {sections.map(([section, entries]) => (
            <div key={section}>
              <div className='sticky top-0 z-10 bg-slate-50 px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-slate-500 dark:bg-darkmode-600'>
                {section}
              </div>

              {entries.map((surface) => {
                const actual = effective[surface.id] ?? 'none';
                const chosenLevel = levels[surface.id] ?? 'none';
                const pulledInBy =
                  actual !== 'none' && actual !== chosenLevel
                    ? reasonFor(surface)
                    : null;

                return (
                  <div
                    key={surface.id}
                    className='border-t border-slate-200/60 px-3 py-2.5 dark:border-darkmode-400'
                  >
                    <div className='flex items-start justify-between gap-3'>
                      <div className='flex min-w-0 items-start gap-2'>
                        {surface.kind === 'toggle' && renderToggle(surface)}
                        <div className='min-w-0'>
                          <div className='flex flex-wrap items-center gap-x-2'>
                            <span
                              className='text-sm font-medium'
                              title={`${surface.where}. ${surface.description}`}
                            >
                              {surface.label}
                            </span>
                            {surface.path && clientSegment && (
                              // New tab: navigating away would lose unsaved work.
                              <a
                                href={`/${clientSegment}${surface.path}`}
                                target='_blank'
                                rel='noreferrer'
                                className='inline-flex items-center gap-0.5 text-[11px] text-primary hover:underline'
                              >
                                Show me
                                <Lucide
                                  icon='ExternalLink'
                                  className='h-3 w-3 shrink-0'
                                />
                              </a>
                            )}
                          </div>
                          {pulledInBy && (
                            <span className='mt-1 flex items-center gap-1 text-[11px] leading-snug text-primary/80'>
                              <Lucide icon='Lock' className='h-3 w-3 shrink-0' />
                              Comes with “{pulledInBy}”
                            </span>
                          )}
                        </div>
                      </div>

                      {surface.kind === 'page' && renderLevelPicker(surface)}
                    </div>

                    {/* Extras only make sense once the screen itself is on. */}
                    {actual !== 'none' && !!surface.addOns?.length && (
                      <div className='mt-2 space-y-1 border-l-2 border-slate-200 pl-3 dark:border-darkmode-400'>
                        {surface.addOns.map((addOn) => (
                          <label
                            key={addOn.key}
                            className={`flex items-start gap-2 text-xs ${
                              readOnly ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
                            }`}
                          >
                            <input
                              type='checkbox'
                              className='mt-0.5'
                              checked={selected.has(addOn.key)}
                              disabled={readOnly}
                              onChange={() => toggleAddOn(addOn.key)}
                            />
                            <span>
                              {addOn.label}
                              {addOn.description && (
                                <span className='mt-0.5 block text-[11px] leading-snug text-slate-400'>
                                  {addOn.description}
                                </span>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {carried.length > 0 && (
          <p className='mt-2 text-xs text-slate-400'>
            This role also holds {carried.length} permission
            {carried.length === 1 ? '' : 's'} with no screen of their own. They
            are kept as they are.
          </p>
        )}
      </div>

      {/* Keepme staff only, and currently off. While off, platform-only keys
          (platform:jobs, clients:list, scope:crossOrganization) cannot be
          granted from the builder — none of them sits on a screen. */}
      {SHOW_ADVANCED && isPlatform && !readOnly && catalogue.length > 0 && (
        <div className='rounded-md border border-slate-200/60 dark:border-darkmode-400'>
          <button
            type='button'
            className='flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-slate-500'
            onClick={() => setShowAdvanced((v) => !v)}
          >
            <Lucide
              icon={showAdvanced ? 'ChevronDown' : 'ChevronRight'}
              className='h-4 w-4'
            />
            Advanced — individual permissions ({advanced.size} added)
          </button>

          {showAdvanced && (
            <div className='max-h-[30vh] overflow-y-auto border-t border-slate-200/60 px-3 py-2 dark:border-darkmode-400'>
              {catalogue.map((mod) => (
                <div key={mod.module} className='mb-2'>
                  <div className='text-[11px] font-medium uppercase tracking-wide text-slate-400'>
                    {mod.label}
                  </div>
                  {mod.permissions.map((perm) => {
                    const on = selected.has(perm.key);
                    // Already granted by a screen above — untickable here.
                    const viaSurface = on && !advanced.has(perm.key);
                    return (
                      <label
                        key={perm.key}
                        className={`flex items-start gap-2 py-0.5 text-xs ${
                          viaSurface ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                        }`}
                      >
                        <input
                          type='checkbox'
                          className='mt-0.5'
                          checked={on}
                          disabled={viaSurface}
                          onChange={() =>
                            setAdvanced((prev) => {
                              const next = new Set(prev);
                              if (next.has(perm.key)) next.delete(perm.key);
                              else next.add(perm.key);
                              return next;
                            })
                          }
                        />
                        <span>
                          {perm.label}
                          <span className='ml-1 font-mono text-[11px] text-slate-400'>
                            {perm.key}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {role && (
        <dl className='grid grid-cols-2 gap-x-6 gap-y-1 rounded-md bg-slate-50 px-3 py-2 text-xs dark:bg-darkmode-700/40'>
          <div className='flex gap-2'>
            <dt className='text-slate-500'>Created</dt>
            <dd>
              {stamp(role.createdAt)}
              <span className='text-slate-500'> by {who(role.createdBy)}</span>
            </dd>
          </div>
          <div className='flex gap-2'>
            <dt className='text-slate-500'>Last updated</dt>
            <dd>
              {stamp(role.updatedAt)}
              <span className='text-slate-500'> by {who(role.updatedBy)}</span>
            </dd>
          </div>
        </dl>
      )}

      <div className='flex justify-end gap-2'>
        <Button variant='outline-secondary' onClick={onCancel}>
          Cancel
        </Button>
        {!readOnly && (
          <Button
            variant='primary'
            disabled={!canSave}
            onClick={() =>
              onSave({
                name: name.trim(),
                description: description.trim(),
                permissions: [...selected],
                surfaces: surfaces
                  .filter((s) => (levels[s.id] ?? 'none') !== 'none')
                  .map((s) => s.id),
              })
            }
          >
            {saving ? 'Saving…' : role ? 'Save changes' : 'Create role'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default RoleBuilder;
